import { Field } from "@/components/app/common";

function GridInput({ q, value = {}, onChange, readOnly }) {
  const set = (ri, cid, v) => onChange({ ...value, [ri]: { ...(value[ri] || {}), [cid]: v } });
  return (
    <div className="overflow-x-auto"><table className="w-full text-sm border border-slate-200 rounded" data-testid={`q-${q.id}`}>
      <thead><tr className="bg-slate-50 text-left">{["", ...q.columns.map((c) => c.label)].map((h, i) => <th key={i} className="p-2 label-sm">{h}</th>)}</tr></thead>
      <tbody>{q.rows.map((r, ri) => (
        <tr key={ri} className="border-t border-slate-100">
          <td className="p-2 font-medium min-w-[180px]">{r}</td>
          {q.columns.map((c) => {
            const v = value[ri]?.[c.id] ?? "";
            if (readOnly) return <td key={c.id} className="p-2">{v || "—"}</td>;
            return <td key={c.id} className="p-1 min-w-[140px]">{c.type === "choice"
              ? <select className="field-input" value={v} onChange={(e) => set(ri, c.id, e.target.value)} data-testid={`q-${q.id}-${ri}-${c.id}`}><option value="">—</option>{c.options.map((o) => <option key={o}>{o}</option>)}</select>
              : <input type={c.type === "date" ? "date" : "text"} className="field-input" value={v} onChange={(e) => set(ri, c.id, e.target.value)} data-testid={`q-${q.id}-${ri}-${c.id}`} />}</td>;
          })}
        </tr>))}</tbody>
    </table></div>
  );
}

function ReadValue({ q, v }) {
  if (q.type === "multi") return <p className="text-sm">{(v || []).join(", ") || "—"}</p>;
  if (q.type === "currency") return <p className="text-sm">{v !== undefined && v !== "" ? `$${v}` : "—"}</p>;
  return <p className="text-sm whitespace-pre-wrap">{v === undefined || v === "" ? "—" : String(v)}</p>;
}

export function QuestionInput({ q, answers, onChange, readOnly }) {
  const v = answers[q.id];
  const set = (val) => onChange({ ...answers, [q.id]: val });
  const detailKey = `${q.id}__detail`;
  const showDetail = q.type === "choice" && (q.detail_when || []).includes(v);
  const lab = <>{q.label}{q.required && !readOnly && <span className="text-red-600"> *</span>}</>;
  if (q.type === "grid") return <div className="sm:col-span-2 space-y-1.5 min-w-0"><p className="label-sm">{lab}</p><GridInput q={q} value={v || {}} onChange={set} readOnly={readOnly} /></div>;
  const wide = ["textarea", "multi"].includes(q.type) || (q.type === "choice" && q.options.length > 3);
  let input;
  if (readOnly) input = <ReadValue q={q} v={v} />;
  else if (q.type === "textarea") input = <textarea className="field-input min-h-[80px]" value={v || ""} onChange={(e) => set(e.target.value)} data-testid={`q-${q.id}`} />;
  else if (q.type === "choice") input = (
    <div className="flex flex-wrap gap-2" data-testid={`q-${q.id}`}>{q.options.map((o) => (
      <button type="button" key={o} onClick={() => set(v === o ? "" : o)} data-testid={`q-${q.id}-opt-${o.replace(/\W+/g, "-").toLowerCase()}`}
        className={`px-3 h-10 rounded-md border text-sm transition-colors ${v === o ? "bg-emerald-800 text-white border-emerald-800" : "bg-white border-slate-300 hover:bg-slate-50"}`}>{o}</button>))}</div>);
  else if (q.type === "multi") input = (
    <div className="flex flex-wrap gap-3" data-testid={`q-${q.id}`}>{q.options.map((o) => (
      <label key={o} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={(v || []).includes(o)} onChange={(e) => set(e.target.checked ? [...(v || []), o] : (v || []).filter((x) => x !== o))} />{o}</label>))}</div>);
  else input = (
    <div className="relative">{q.type === "currency" && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>}
      <input type={q.type === "date" ? "date" : ["number", "currency"].includes(q.type) ? "number" : "text"} className={`field-input ${q.type === "currency" ? "pl-7" : ""}`} value={v ?? ""} onChange={(e) => set(e.target.value)} data-testid={`q-${q.id}`} /></div>);
  return (
    <div className={`min-w-0 ${wide ? "sm:col-span-2" : ""}`}>
      <Field label={lab} hint={q.help}>{input}</Field>
      {showDetail && <div className="mt-2"><Field label={q.detail_label || "Details"}>{readOnly ? <ReadValue q={{ type: "text" }} v={answers[detailKey]} />
        : <textarea className="field-input min-h-[60px]" value={answers[detailKey] || ""} onChange={(e) => onChange({ ...answers, [detailKey]: e.target.value })} data-testid={`q-${q.id}-detail`} />}</Field></div>}
    </div>
  );
}

export function IntakeSections({ sections, answers, onChange, readOnly, audience = "owner" }) {
  return sections.filter((s) => (s.audience || "owner") === audience).map((s) => (
    <section key={s.id} className="panel p-5 sm:p-6 min-w-0" data-testid={`intake-section-${s.id}`}>
      <h2 className="text-base md:text-lg font-semibold">{s.title}</h2>
      {s.intro && <p className="text-xs text-slate-600 mt-1">{s.intro}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 min-w-0">{s.questions.map((q) => <QuestionInput key={q.id} q={q} answers={answers} onChange={onChange} readOnly={readOnly} />)}</div>
    </section>
  ));
}
